export default {
  slug: "/hair-implant-hybrid",
  group: { href: "/hair", label: { en: "Hair restoration", ar: "زراعة الشعر" } },
  heroImage: "/assets/images/ai/services/hair/techniques-compare.webp",

  en: {
    title: "Hybrid Hair Transplant (FUE + FUT) in Istanbul",
    eyebrow: "Hair restoration",
    lead:
      "A hybrid session uses both ways of taking hair — a fine strip from the back of the scalp and " +
      "individual follicular units — in one operation, when one method alone cannot deliver the number of " +
      "grafts the bald area needs.",
    cta: "Book a free assessment",
    heroAlt: "Hybrid hair transplant technique comparison at Platinya Clinic",
    stats: [
      { value: "1 session", label: "Two techniques" },
      { value: "+30–50%", label: "Grafts vs FUE alone" },
      { value: "12 months", label: "To judge the result" },
    ],
    candidate: {
      heading: "Who it is for",
      intro:
        "The hybrid is a supply problem solved with two tools: it exists for large bald areas and a donor " +
        "area that is not dense enough to give everything by extraction alone.",
      items: [
        "Advanced loss — Norwood 5 to 6 — where a single FUE session would leave the crown thin",
        "Good density in the strip area but limited follicular units for extraction",
        "A previous FUE session that used up the accessible donor sites",
        "Willingness to accept a fine linear scar in exchange for substantially more grafts",
      ],
      note:
        "More grafts is not automatically better. The hybrid makes sense when the plan genuinely needs the " +
        "extra numbers; for smaller areas a single technique is simpler and heals faster.",
    },
    techniques: {
      heading: "How the two halves work together",
      intro: "The strip is taken first, then the extraction fills the rest of the plan — one anaesthetic, one day.",
      items: [
        {
          name: "The strip (FUT)",
          text:
            "A narrow ellipse of scalp is removed from the back, closed in layers, and dissected into " +
            "individual grafts under a microscope. It is the densest source of grafts per square centimetre.",
        },
        {
          name: "The extraction (FUE)",
          text:
            "Follicles are then punched individually from the sides, adding grafts without another scar " +
            "line — and, importantly, without thinning the closure of the strip.",
        },
        {
          name: "Why not simply do more FUE",
          text:
            "Because over-harvesting a donor area leaves it patchy and closes the door to any future session. " +
            "The hybrid spreads the load across two donor zones.",
        },
      ],
    },
    journey: {
      heading: "From planning to the final result",
      steps: [
        {
          name: "Graft budgeting",
          text:
            "Donor density in the strip zone and the extraction zone is measured separately, and the number " +
            "of grafts each can give is added up. That total sets the plan.",
        },
        {
          name: "Surgery day",
          text:
            "Strip removal takes about an hour; the team dissects while extraction continues. Expect eight " +
            "to ten hours in total for a large session.",
        },
        {
          name: "First two weeks",
          text:
            "The strip line is closed with sutures or staples, removed around day 10–14. Crusts in the " +
            "recipient area clear in the same period.",
        },
        {
          name: "Months 1–3",
          text: "Transplanted hair sheds, then regrowth begins. The linear scar fades from red to pale.",
        },
        {
          name: "Month 12–18",
          text:
            "Final density. At normal hair length the strip scar is usually covered by the surrounding hair; " +
            "very short styles will show it.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro: "The hybrid carries the risks of both techniques at once.",
      items: [
        "A permanent linear scar across the back of the scalp, hidden by hair at normal length",
        "Tightness or a stretch in the scar if the closure is under tension or healing is poor",
        "Temporary numbness above the scar that can take months to settle",
        "The usual risks of any transplant: swelling, folliculitis, partial graft loss",
        "A long session — fatigue affects both the patient and the team, which is why the day is planned in stages",
      ],
      note:
        "Ask who closes the strip and how many hybrid sessions the clinic performs a month. The closure is " +
        "the part patients judge years later.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "How many grafts can a hybrid give?",
          a:
            "Commonly 4,000–6,000 in one session for a suitable donor area, against roughly 2,500–3,500 from " +
            "FUE alone. The exact figure is measured, not promised.",
        },
        {
          q: "Will the scar show?",
          a:
            "At normal hair length, usually not. With a shaved head or a very short fade it is visible as a " +
            "pale line, and that must be accepted before booking.",
        },
        {
          q: "Is it more painful than FUE?",
          a:
            "The first two nights are tighter because of the closure. After that the two sides feel similar; " +
            "the strip area is often described as a bruised ache.",
        },
        {
          q: "Can I have FUE later?",
          a:
            "Yes, if the extraction zones were not exhausted. That is exactly why the plan budgets the donor " +
            "area across future sessions rather than spending it all at once.",
        },
      ],
    },
    ctaBand: {
      heading: "Send your photographs for a first opinion",
      text: "We will measure both donor zones and tell you whether a hybrid adds anything over a single technique.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability and graft numbers are decided after examination; results vary.",
  },

  ar: {
    title: "زراعة الشعر المختلطة (FUE + FUT) في إسطنبول",
    eyebrow: "زراعة الشعر",
    lead:
      "الجلسة المختلطة تستخدم طريقتي أخذ الشعر معاً — شريط رفيع من مؤخرة الرأس وبصيلات مفردة — في عملية واحدة، " +
      "حين لا تكفي طريقة واحدة لتأمين عدد البصيلات الذي تحتاجه المنطقة الفارغة.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "مقارنة تقنيات زراعة الشعر في عيادة بلاتينيا",
    stats: [
      { value: "جلسة واحدة", label: "تقنيتان معاً" },
      { value: "‎+٣٠–٥٠٪", label: "بصيلات أكثر من FUE" },
      { value: "١٢ شهراً", label: "للحكم على النتيجة" },
    ],
    candidate: {
      heading: "لمن تُناسب",
      intro:
        "المختلطة حلّ لمشكلة مخزون بأداتين: وُجدت للمناطق الفارغة الكبيرة ولمنطقة مانحة لا تكفي كثافتها " +
        "لتقديم كل شيء بالاقتطاف وحده.",
      items: [
        "تساقط متقدّم — نوروود ٥ إلى ٦ — حيث تترك جلسة FUE واحدة التاج خفيفاً",
        "كثافة جيدة في منطقة الشريط لكن بصيلات اقتطاف محدودة",
        "جلسة FUE سابقة استهلكت مواقع الاقتطاف المتاحة",
        "قبول بأثر خطّي رفيع مقابل عدد بصيلات أكبر بكثير",
      ],
      note:
        "زيادة البصيلات ليست أفضلية تلقائية. المختلطة منطقية حين تحتاج الخطة الأرقام الإضافية فعلاً؛ " +
        "أما المناطق الأصغر فتقنية واحدة أبسط وتشفى أسرع.",
    },
    techniques: {
      heading: "كيف يعمل النصفان معاً",
      intro: "يُؤخذ الشريط أولاً ثم يكمل الاقتطاف بقية الخطة — تخدير واحد ويوم واحد.",
      items: [
        {
          name: "الشريط (FUT)",
          text:
            "يُزال شكل بيضاوي رفيع من مؤخرة الرأس ويُغلق بطبقات ويُفصل تحت المجهر إلى بصيلات مفردة. " +
            "وهو أكثف مصدر للبصيلات لكل سنتيمتر مربع.",
        },
        {
          name: "الاقتطاف (FUE)",
          text:
            "ثم تُقتطف بصيلات مفردة من الجانبين، فتضيف بصيلات بلا خطّ أثر ثانٍ — والأهم بلا إضعاف إغلاق الشريط.",
        },
        {
          name: "لماذا لا نكتفي بمزيد من FUE",
          text:
            "لأن الاستهلاك المفرط للمنطقة المانحة يتركها غير متساوية ويغلق باب أي جلسة مستقبلية. " +
            "المختلطة توزّع الحِمل على منطقتين مانحتين.",
        },
      ],
    },
    journey: {
      heading: "من التخطيط إلى النتيجة النهائية",
      steps: [
        {
          name: "ميزانية البصيلات",
          text:
            "تُقاس كثافة منطقة الشريط ومنطقة الاقتطاف كلٌّ على حدة، ويُجمع ما تستطيع كل منهما تقديمه. " +
            "هذا المجموع يحدّد الخطة.",
        },
        {
          name: "يوم العملية",
          text:
            "إزالة الشريط تستغرق نحو ساعة، ويُفصل الفريق البصيلات بينما يستمر الاقتطاف. " +
            "توقّع ثماني إلى عشر ساعات للجلسة الكبيرة.",
        },
        {
          name: "أول أسبوعين",
          text:
            "يُغلق خط الشريط بالغرز أو الدبابيس وتُزال حول اليوم العاشر إلى الرابع عشر. " +
            "وتزول قشور المنطقة المزروعة في الفترة نفسها.",
        },
        {
          name: "الشهر الأول إلى الثالث",
          text: "يتساقط الشعر المزروع ثم يبدأ النمو. ويبهت الأثر الخطّي من الأحمر إلى الفاتح.",
        },
        {
          name: "الشهر ١٢–١٨",
          text:
            "الكثافة النهائية. بطول الشعر العادي يغطّي الشعر المحيط أثر الشريط عادةً؛ " +
            "أما القصّات القصيرة جداً فتُظهره.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro: "المختلطة تحمل مخاطر التقنيتين معاً.",
      items: [
        "أثر خطّي دائم في مؤخرة الرأس، يخفيه الشعر بالطول العادي",
        "شدّ أو تمدّد في الأثر إذا كان الإغلاق تحت توتر أو الشفاء ضعيفاً",
        "تنميل مؤقّت فوق الأثر قد يستغرق أشهراً ليهدأ",
        "مخاطر أي زراعة: تورّم، التهاب جريبات، فقدان جزئي للبصيلات",
        "جلسة طويلة — الإجهاد يصيب المريض والفريق، ولهذا يُخطَّط اليوم على مراحل",
      ],
      note:
        "اسأل من يُغلق الشريط وكم جلسة مختلطة تُجريها العيادة شهرياً. الإغلاق هو ما يحكم عليه المريض بعد سنوات.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "كم بصيلة تعطي الجلسة المختلطة؟",
          a:
            "غالباً ٤٠٠٠–٦٠٠٠ في جلسة واحدة لمنطقة مانحة مناسبة، مقابل نحو ٢٥٠٠–٣٥٠٠ بـFUE وحدها. " +
            "الرقم يُقاس ولا يُوعَد به.",
        },
        {
          q: "هل يظهر الأثر؟",
          a:
            "بطول الشعر العادي لا يظهر عادة. مع الرأس المحلوق أو القصّة القصيرة جداً يظهر كخط فاتح، " +
            "وهذا يجب قبوله قبل الحجز.",
        },
        {
          q: "هل هي أكثر إيلاماً من FUE؟",
          a:
            "الليلتان الأوليان أشدّ بسبب الإغلاق. بعدها يتشابه الجانبان؛ ويُوصف موضع الشريط كألم رَضّي.",
        },
        {
          q: "هل يمكنني إجراء FUE لاحقاً؟",
          a:
            "نعم إذا لم تُستهلك مناطق الاقتطاف. ولهذا توزّع الخطة ميزانية المنطقة المانحة على جلسات مستقبلية " +
            "بدل إنفاقها كلها مرة واحدة.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورك لرأي أوّلي",
      text: "سنقيس المنطقتين المانحتين ونقول هل تضيف المختلطة شيئاً على تقنية واحدة.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. تُحدَّد الملاءمة وعدد البصيلات بعد الفحص؛ والنتائج تختلف.",
  },
};
